import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import './StudentUsernameModal.css'

// Blocking gate for a self-created student. Study Helper stays underneath,
// but this dialog has no close, skip, or backdrop dismiss. It leaves only
// after completeStudentUsername persists the name.
const StudentUsernameModal = ({ onComplete }) => {
  const { completeStudentUsername } = useAuth()
  const [username, setUsername] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const inputRef = useRef(null)
  const dialogRef = useRef(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    if (inputRef.current) inputRef.current.focus()

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      if (event.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return
      const enabled = [...dialog.querySelectorAll('input, button')]
        .filter((node) => !node.disabled)
      if (enabled.length === 0) return
      const first = enabled[0]
      const last = enabled[enabled.length - 1]
      const active = document.activeElement
      if (!dialog.contains(active)) {
        event.preventDefault()
        first.focus()
        return
      }
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = ''
    }
  }, [])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submitting) return

    setSubmitting(true)
    setError('')
    try {
      await completeStudentUsername(username)
      if (onComplete) await onComplete()
    } catch (submitError) {
      if (isMountedRef.current) {
        setError(submitError.message || 'Could not save that username')
      }
    }
    if (isMountedRef.current) {
      setSubmitting(false)
    }
  }

  return (
    <div className='student-username-modal-overlay'>
      <div
        ref={dialogRef}
        className='student-username-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='student-username-modal-title'
        aria-describedby='student-username-modal-body'
      >
        <h2 id='student-username-modal-title' className='student-username-modal-title'>
          Choose a username
        </h2>
        <p id='student-username-modal-body' className='student-username-modal-body'>
          Don't use your real name, email, phone number, school, or address. Pick something that isn't personally identifiable.
        </p>

        <form className='student-username-modal-form' onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type='text'
            name='username'
            value={username}
            onChange={(event) => {
              setUsername(event.target.value)
              if (error) setError('')
            }}
            className='student-username-modal-input'
            disabled={submitting}
            autoFocus
            autoComplete='off'
            autoCapitalize='none'
            autoCorrect='off'
            spellCheck={false}
            maxLength={80}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? 'student-username-modal-error' : 'student-username-modal-body'}
          />
          {error && (
            <p id='student-username-modal-error' className='student-username-modal-error' role='alert'>
              {error}
            </p>
          )}
          <button
            type='submit'
            className='student-username-modal-submit'
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default StudentUsernameModal
